/**
 * Settings: language / timezone / currency (QM-D08-001, QM-D08-003), theme + reduced motion
 * (QM-NC02-003), notification channel preferences + daily digest (QM-D14-001/002), install app (QM-D19-001).
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { NOTIFICATION_CATEGORIES, type Preferences } from '@qm/contracts/notifications';
import { CurrencySchema } from '@qm/contracts/localization';
import { Button, ErrorState, Field, Heading, Loading, Panel, Select, Text, TextInput, Toggle } from '@qm/ui';
import { useApp } from '../../../lib/app-context';
import { bffJson } from '../../../lib/bff';
import { useResource } from '../../../lib/resource';
import { InstallApp } from '../home/InstallApp';

const TIMEZONES = ['Asia/Dubai', 'Asia/Riyadh', 'Asia/Qatar', 'Asia/Kuwait', 'Asia/Bahrain', 'Asia/Muscat', 'Europe/London', 'UTC'];

export function SettingsPage() {
  const { t, locale, localePrefs, setLocalePrefs, ui, setUi } = useApp();
  return (
    <section className="qm-page" aria-labelledby="page-title">
      <Heading level={1} id="page-title">{t('shell.screens.settings.title')}</Heading>
      <Link to="/setup">{t('shell.nav.setup')}</Link>
      <Link to="/settings/security">{t('shell.screens.security.title')}</Link>
      <div className="qm-grid">
        <Panel aria-labelledby="set-lang">
          <Heading level={2} id="set-lang">{t('shell.settings.region')}</Heading>
          <Field label={t('shell.settings.language')}>
            {({ id }) => (
              <Select id={id} value={locale} onChange={(e) => void setLocalePrefs({ locale: e.target.value as 'en' | 'ar' })}>
                <option value="en" lang="en">English</option>
                <option value="ar" lang="ar">العربية</option>
              </Select>
            )}
          </Field>
          <Field label={t('shell.settings.timezone')}>
            {({ id }) => (
              <Select id={id} value={localePrefs.timezone} onChange={(e) => void setLocalePrefs({ timezone: e.target.value })}>
                {TIMEZONES.map((z) => <option key={z} value={z}>{z}</option>)}
              </Select>
            )}
          </Field>
          <Field label={t('shell.settings.currency')}>
            {({ id }) => (
              <Select id={id} value={localePrefs.currency} onChange={(e) => void setLocalePrefs({ currency: e.target.value as typeof localePrefs.currency })}>
                {CurrencySchema.options.map((c) => <option key={c} value={c}>{c}</option>)}
              </Select>
            )}
          </Field>
        </Panel>
        <Panel aria-labelledby="set-display">
          <Heading level={2} id="set-display">{t('shell.settings.display')}</Heading>
          <Field label={t('shell.settings.theme')}>
            {({ id }) => (
              <Select id={id} value={ui.theme} onChange={(e) => void setUi({ theme: e.target.value as typeof ui.theme })}>
                <option value="system">{t('shell.settings.theme.system')}</option>
                <option value="light">{t('shell.settings.theme.light')}</option>
                <option value="dark">{t('shell.settings.theme.dark')}</option>
              </Select>
            )}
          </Field>
          <Field label={t('shell.settings.motion')} hint={t('shell.settings.motionHint')}>
            {({ id, describedBy }) => (
              <Select id={id} aria-describedby={describedBy} value={ui.motion} onChange={(e) => void setUi({ motion: e.target.value as typeof ui.motion })}>
                <option value="system">{t('shell.settings.motion.system')}</option>
                <option value="reduce">{t('shell.settings.motion.reduce')}</option>
                <option value="no-preference">{t('shell.settings.motion.full')}</option>
              </Select>
            )}
          </Field>
        </Panel>
        <InstallApp />
      </div>
      <NotificationPreferences />
    </section>
  );
}

function NotificationPreferences() {
  const { t } = useApp();
  const res = useResource<Preferences>('/v1/notifications/preferences');
  const [prefs, setPrefs] = useState<Preferences | null>(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(false);
  useEffect(() => { if (res.status === 'ready') setPrefs(res.data); }, [res.status, res.data]);
  if (res.status === 'loading' || (!prefs && res.status !== 'error')) return <Loading label={t('common.loading')} />;
  if (!prefs) return <ErrorState message={t('shell.error.load')} />;
  const setChannel = (cat: keyof Preferences['channels'], ch: 'in_app' | 'email', v: boolean) =>
    setPrefs({ ...prefs, channels: { ...prefs.channels, [cat]: { ...prefs.channels[cat], [ch]: v } } });
  const save = async () => {
    setSaved(false); setError(false);
    try {
      setPrefs(await bffJson<Preferences>('/v1/notifications/preferences', 'PUT', { channels: prefs.channels, digest: prefs.digest }));
      setSaved(true);
    } catch { setError(true); }
  };
  return (
    <Panel id="notifications" aria-labelledby="set-notif">
      <Heading level={2} id="set-notif">{t('notifications.prefs.title')}</Heading>
      <Text tone="muted">{t('notifications.prefs.intro')}</Text>
      <table className="qm-prefs">
        <caption className="qm-sr-only">{t('notifications.prefs.title')}</caption>
        <thead><tr><th scope="col">{t('notifications.prefs.category')}</th><th scope="col">{t('notifications.prefs.inApp')}</th><th scope="col">{t('notifications.prefs.email')}</th></tr></thead>
        <tbody>
          {NOTIFICATION_CATEGORIES.map((cat) => {
            const locked = prefs.mandatory.includes(cat);
            const label = t(`notifications.category.${cat}`);
            return (
              <tr key={cat}>
                <th scope="row">{label}{locked ? <span className="qm-text" data-tone="muted"> ({t('notifications.prefs.lockedMandatory')})</span> : null}</th>
                <td><Toggle label={`${label}: ${t('notifications.prefs.inApp')}`} checked={prefs.channels[cat].in_app} disabled={locked} onChange={(v) => setChannel(cat, 'in_app', v)} /></td>
                <td><Toggle label={`${label}: ${t('notifications.prefs.email')}`} checked={prefs.channels[cat].email} disabled={locked} onChange={(v) => setChannel(cat, 'email', v)} /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <Heading level={3}>{t('notifications.digest.title')}</Heading>
      <div className="qm-row">
        <Toggle label={t('notifications.digest.enabled')} checked={prefs.digest.enabled} onChange={(v) => setPrefs({ ...prefs, digest: { ...prefs.digest, enabled: v } })} />
        <span aria-hidden="true">{t('notifications.digest.enabled')}</span>
      </div>
      <Field label={t('notifications.digest.time')}>
        {({ id }) => <TextInput id={id} type="time" value={prefs.digest.time} onChange={(e) => setPrefs({ ...prefs, digest: { ...prefs.digest, time: e.target.value } })} />}
      </Field>
      <Field label={t('notifications.digest.timezone')}>
        {({ id }) => (
          <Select id={id} value={prefs.digest.timezone} onChange={(e) => setPrefs({ ...prefs, digest: { ...prefs.digest, timezone: e.target.value } })}>
            {TIMEZONES.map((z) => <option key={z} value={z}>{z}</option>)}
          </Select>
        )}
      </Field>
      <Button variant="primary" onClick={() => void save()}>{t('common.save')}</Button>
      {saved ? <p role="status">{t('notifications.prefs.saved')}</p> : null}
      {error ? <ErrorState message={t('shell.error.action')} /> : null}
    </Panel>
  );
}
